import React, {
  createContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { AppReducer } from "../Reducers/AppReducer";
import {
  AppContextInitialState,
  HTTPResponse,
  ToastTimeout,
  TopMenuItems,
  SideMenuPageList,
} from "../Helpers/Enums";
import Loader from "../Components/Loader";
import Toast from "../Components/Toast";
import { AppReducerActions } from "../ReducerActions/AppReducerActions";
import ActionPopUp from "../Components/ActionPopUp";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useLocation, useNavigate } from "react-router-dom";
import TopMenu from "../Components/TopMenu";
import { CommonMsgs } from "../Helpers/HelperText";
import { GetLoggedInUserID, isStringEmpty } from "../Helpers/Utilities";
import { UserLoginService } from "../WebApiServices/UserLogin.service";
import PageLayout from "../Components/PageLayout";

export const AppContext = createContext();
const AppContextProvider = (props) => {
  const [appState, DispatchAppContextAction] = useReducer(AppReducer, {
    ...AppContextInitialState,
  });
  const navigate = useNavigate();
  const { pathname } = useLocation();

  //ref to resolve the confirm action
  const confirmResolveFunction = useRef();

  //ref to always hold latest save function from current page
  const savePageRef = useRef(null);

  /* <summary>
  date: 06-04-2024 
  Name: AP
  description: Effect to hide toast after 4 seconds
  <summary>*/
  useEffect(() => {
    if (appState.showToast) {
      setTimeout(() => {
        DispatchAppContextAction({
          type: AppReducerActions.SHOW_TOAST,
          message: "",
        });
      }, ToastTimeout);
    }
    return () => {};
  }, [appState]);

  useEffect(() => {
    const ptRoutes = [...SideMenuPageList.map((a) => a.path?.toLowerCase())];
    const includedRoutes = [
      "/managepatients",
      "/manageabstractors",
      "/managecenters",
      "/centerinformation",
      "/managerounds",
      "/chartsummary",
      ...ptRoutes,
    ];

    window.scrollTo(0, 0);
    if (
      !isStringEmpty(pathname) &&
      includedRoutes.includes(pathname.toLowerCase())
    ) {
      ShowTopMenu(true);

      if (
        pathname.toLowerCase().includes("/managecenters") ||
        pathname.toLowerCase().includes("/centerinformation") ||
        pathname.toLowerCase().includes("/managerounds")
      )
        SetActiveTab(TopMenuItems.Centers);
      else if (pathname.toLowerCase().includes("/manageabstractors"))
        SetActiveTab(TopMenuItems.Abstractors);
      else if (pathname.toLowerCase().includes("/chartsummary"))
        SetActiveTab(TopMenuItems.ChartSummary);
      else SetActiveTab(TopMenuItems.Patients);
    } else ShowTopMenu(false);

    async function Authenticate() {
      const response = await UserLoginService.AuthenticateJWTToken();
      if (response?.status === HTTPResponse.Unauthorized) {
        localStorage.clear();
        const excludeRoutes = ["/", "/resetpassword", "/passwordrecovery"];
        if (!excludeRoutes.includes(pathname.toLowerCase())) {
          await HandleSessionTimeout();
        }
      }
    }
    GetLoggedInUserID() && Authenticate();
  }, [pathname]);

  /* <summary>
  date: 06-04-2024
  Name: AP
  description: Show message n Redirect to login page once session times out
  <summary>*/
  const HandleSessionTimeout = async () => {
    ToggleLoader(false);
    const choice = await ShowActionPopUp(
      "",
      CommonMsgs.SessionTimeout,
      "Ok",
      true,
    );
    if (choice) {
      navigate("/");
    }
  };

  /* <summary>
  date: 06-04-2024
  Name: AP
  description: Toggle UI Loader
  <summary>*/
  function ToggleLoader(action) {
    DispatchAppContextAction({
      type: AppReducerActions.TOGGLE_LOADER,
      value: action,
    });
  }

  function ShowToast(message, toastType) {
    DispatchAppContextAction({
      type: AppReducerActions.SHOW_TOAST,
      message,
      toastType,
    });
  }

  /* <summary>
  date: 06-04-2024
  Name: AP
  description: Method to show confirm modal and resolve the action
  <summary>*/
  const ShowActionPopUp = (
    title,
    description,
    confirmButtonLabel,
    disableCancelBtn,
    cancelButtonLabel = "Cancel",
  ) => {
    return new Promise((resolve) => {
      DispatchAppContextAction({
        type: AppReducerActions.SHOW_ACTIONPOPUP,
        title,
        description,
        confirmButtonLabel,
        disableCancelBtn,
        cancelButtonLabel,
      });
      confirmResolveFunction.current = (choice) => {
        resolve(choice);
        DispatchAppContextAction({
          type: AppReducerActions.CLOSE_ACTIONPOPUP,
        });
      };
    });
  };

  const OnConfirmModalCancelClicked = () => {
    confirmResolveFunction.current(false);
  };

  const OnConfirmModalConfirmClicked = () => {
    confirmResolveFunction.current(true);
  };

  /* <summary>
  date: 06-04-2024
  Name: AP
  description: Toggle top menu
  <summary>*/
  const ShowTopMenu = (action) => {
    DispatchAppContextAction({
      type: AppReducerActions.SHOW_TOPMENU,
      value: action,
    });
  };

  /* <summary>
date: 06-04-2024
Name: AP
description: Set active tab to highlight in top menu
<summary>*/
  const SetActiveTab = (action) => {
    DispatchAppContextAction({
      type: AppReducerActions.SET_ACTIVETAB,
      value: action,
    });
  };

  const SetPatientConfigData = (data) => {
    DispatchAppContextAction({
      type: AppReducerActions.SET_PATIENT_CONFIG_DATA,
      data,
    });
  };

  const setIsSideMenuActive = (data) => {
    DispatchAppContextAction({
      type: AppReducerActions.SET_ISACTIVE_SIDEMENU,
      value: data,
    });
  };

  const registerPageConfig = (config) => {
    DispatchAppContextAction({
      type: AppReducerActions.SET_PAGE_CONFIG,
      value: config,
    });
  };

  const registerSaveFn = (fn) => {
    savePageRef.current = fn;
  };

  const { isLoading, showToast, toastMessage, actionPopUp, showTopMenu } =
    appState;

  const value = useMemo(() => {
    return {
      appState,
      ToggleLoader,
      ShowToast,
      ShowActionPopUp,
      ShowTopMenu,
      SetActiveTab,
      HandleSessionTimeout,
      SetPatientConfigData,
      patientConfigData: appState.patientConfigData,
      setIsSideMenuActive,
      isSideMenuActive: appState.isSideMenuActive,
      registerPageConfig,
      registerSaveFn,
      savePageRef,
    };
  }, [appState]);

  const ptRoutes = (SideMenuPageList || [])
    .filter((a) => a?.path)
    .map((a) => a.path.toLowerCase());

  const shouldUseLayout =
    !!pathname && ptRoutes.includes(pathname.toLowerCase());

  return (
    <AppContext.Provider value={value}>
      <>
        {isLoading && <Loader />}
        {showToast && toastMessage && <Toast />}
        {actionPopUp.isOpen && (
          <ActionPopUp
            onCancel={OnConfirmModalCancelClicked}
            OnConfirmed={OnConfirmModalConfirmClicked}
          />
        )}
        <main
          className="gradient-custom"
          style={pathname === "/" ? { overflowY: "hidden" } : {}}
        >
          <Header />
          {showTopMenu && <TopMenu />}
          {shouldUseLayout ? (
            <PageLayout>{props.children}</PageLayout>
          ) : (
            props.children
          )}
          {<Footer isLoginPage={pathname.toLowerCase() === "/"} />}
        </main>
      </>
    </AppContext.Provider>
  );
};

export default AppContextProvider;
